import React, { useState, useEffect, useLayoutEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import useAxios from "./useAxios";
import {
	refreshToken,
	hasStoredSession,
	clearLocalAuth,
	getCurrentUserFromToken,
	getCurrentUserProfile,
	ensureValidSession,
	isDefinitiveAuthFailure,
	USER_CLASSIFICATION_NAME_KEY,
} from "./AuthService";

const CHANGE_INITIAL_PASSWORD_PATH = "/change-initial-password";

const PrivateRoute = ({
	children,
	requiredGroups = [],
	requiredClassifications = [],
	deniedClassifications = [],
}) => {
	const [isAuthenticated, setIsAuthenticated] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const location = useLocation();
	const [hasAccess, setHasAccess] = useState(null);

	const groupsKey = requiredGroups.join("\0");
	const reqClassKey = requiredClassifications.join("\0");
	const denyClassKey = deniedClassifications.join("\0");
	const needRules =
		requiredGroups.length > 0 ||
		requiredClassifications.length > 0 ||
		deniedClassifications.length > 0;

	useEffect(() => {
		const checkAuth = async () => {
			if (!hasStoredSession()) {
				setIsAuthenticated(false);
				setIsLoading(false);
				return;
			}
			try {
				const sessionReady = await ensureValidSession();
				if (!sessionReady) {
					setIsAuthenticated(false);
					setIsLoading(false);
					return;
				}
				await useAxios.get("/users/token/verify/");
				setIsAuthenticated(true);
			} catch (error) {
				if (error.response?.status === 401) {
					try {
						await refreshToken();
						await useAxios.get(
							"/users/token/verify/",
						);
						setIsAuthenticated(true);
					} catch (refreshError) {
						if (isDefinitiveAuthFailure(refreshError)) {
							clearLocalAuth();
							setIsAuthenticated(false);
						} else {
							setIsAuthenticated(hasStoredSession());
						}
					}
				} else {
					setIsAuthenticated(hasStoredSession());
				}
			} finally {
				setIsLoading(false);
			}
		};

		checkAuth();
	}, []);

	useLayoutEffect(() => {
		if (isAuthenticated === true && !needRules) {
			setHasAccess(true);
		}
	}, [isAuthenticated, needRules]);

	useEffect(() => {
		if (isAuthenticated !== true) {
			return;
		}
		if (!needRules) {
			setHasAccess(true);
			return;
		}

		let cancelled = false;

		const run = async () => {
			setHasAccess(null);

			if (requiredGroups.length > 0) {
				const groupStr = localStorage.getItem("user_groups");
				let groups = [];
				try {
					groups = groupStr ? JSON.parse(groupStr) : [];
				} catch {
					groups = [];
				}
				const inGroup = groups.some((g) =>
					requiredGroups.includes(g),
				);
				if (!inGroup) {
					if (!cancelled) setHasAccess(false);
					return;
				}
			}

			if (
				requiredClassifications.length > 0 ||
				deniedClassifications.length > 0
			) {
				let name = localStorage.getItem(
					USER_CLASSIFICATION_NAME_KEY,
				);
				if (name === null) {
					try {
						const profile = await getCurrentUserProfile();
						name = profile.classification_name ?? "";
						localStorage.setItem(
							USER_CLASSIFICATION_NAME_KEY,
							name,
						);
					} catch {
						if (!cancelled) setHasAccess(false);
						return;
					}
				}
				if (
					requiredClassifications.length > 0 &&
					!requiredClassifications.includes(name)
				) {
					if (!cancelled) setHasAccess(false);
					return;
				}
				if (
					deniedClassifications.length > 0 &&
					name &&
					deniedClassifications.includes(name)
				) {
					if (!cancelled) setHasAccess(false);
					return;
				}
			}

			if (!cancelled) setHasAccess(true);
		};

		run();

		return () => {
			cancelled = true;
		};
	}, [
		isAuthenticated,
		groupsKey,
		reqClassKey,
		denyClassKey,
		needRules,
	]);

	if (
		isLoading ||
		(isAuthenticated === true && hasAccess === null)
	) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
					flexDirection: "column",
					gap: "16px",
				}}>
				<div
					style={{
						fontSize: "16px",
						color: "#666",
					}}>
					인증 확인 중...
				</div>
			</div>
		);
	}

	if (isAuthenticated) {
		const payload = getCurrentUserFromToken();
		const mustChange = payload?.must_change_password === true;
		if (
			mustChange &&
			location.pathname !== CHANGE_INITIAL_PASSWORD_PATH
		) {
			return (
				<Navigate
					to={CHANGE_INITIAL_PASSWORD_PATH}
					replace
				/>
			);
		}
	}

	if (isAuthenticated && hasAccess === false) {
		return (
			<div
				style={{
					textAlign: "center",
					marginTop: 40,
					color: "red",
				}}>
				접근 권한이 없습니다.
			</div>
		);
	}

	if (!isAuthenticated) {
		return (
			<Navigate
				to="/login"
				state={{ from: location }}
				replace
			/>
		);
	}

	if (hasAccess === true) {
		return children;
	}

	return null;
};

export default PrivateRoute;
